import type { TFunction } from 'i18next';
import type { FetchWorkReportsDto } from '@repo/api';
import {
  isWorkReportBillingState,
  toViewerWorkReportBillingState,
  workReportBillingStatesFor,
  type WorkReportBillingState,
  type WorkReportBillingViewer,
} from '@repo/utils';
import type { ActiveFilterChip } from '@repo/ui/components/molecules/infinite-table';
import type { DateRangeValue } from '@repo/ui/lib/date-range-param';
import {
  LIST_FILTER_ALL,
  toFilterFormValue,
  toFilterParam,
} from '@/lib/filters/filter-sentinel';
import {
  EMPTY_PERIOD,
  hasPeriod,
  toPeriodFormValue,
  toPeriodParams,
} from '@/lib/filters/panel-params';
import { formatPeriodChipValue } from '@/lib/filters/panel-chips';
import {
  panelSearchFields,
  panelSearchParam,
} from '@/lib/search-params/panel-search-param';
import { billingStateLabelKey } from './status';

/**
 * Stati di fatturazione filtrabili da chi guarda, nell'ordine del pannello:
 * quelli condivisi (`@repo/utils`, «Stornato» compreso) con «Nessun
 * addebito» in fondo. Senza la sezione Fatture «In bozza fattura» non c'è:
 * è fuso in «In fattura».
 */
export function workReportBillingFiltersFor(
  viewer: WorkReportBillingViewer
): readonly WorkReportBillingState[] {
  const states = workReportBillingStatesFor(viewer);
  return [...states.filter((state) => state !== 'NONE'), 'NONE'];
}

const isBillingState = isWorkReportBillingState;

/**
 * «I miei» nell'URL: booleano, presente solo acceso. I link scritti prima
 * avevano la stringa `'true'` e valgono ancora.
 */
const LEGACY_MINE_PARAM = 'true';

export const isMineOn = (value: unknown): boolean =>
  value === true || value === LEGACY_MINE_PARAM;

/**
 * Campi del pannello Filtri della lista rapportini (tavola 8a): Cliente,
 * Contratto e Commessa (solo con le rispettive sezioni), Tecnico, «I miei»,
 * Fatturazione e Periodo (data del rapportino). Lo stato resta ai tab.
 */
export interface WorkReportFilterValues {
  companyId: string;
  contractId: string;
  commessaId: string;
  technicianId: string;
  mine: boolean | null;
  billingState: string;
  period: DateRangeValue;
}

export const EMPTY_WORK_REPORT_FILTERS: WorkReportFilterValues = {
  companyId: LIST_FILTER_ALL,
  contractId: LIST_FILTER_ALL,
  commessaId: LIST_FILTER_ALL,
  technicianId: LIST_FILTER_ALL,
  mine: null,
  billingState: LIST_FILTER_ALL,
  period: EMPTY_PERIOD,
};

/** Parametri dell'URL letti dalla lista (nomi uguali a quelli dell'API). */
export interface WorkReportFilterSearch {
  companyId?: string | null;
  contractId?: string | null;
  commessaId?: string | null;
  technicianId?: string | null;
  mine?: string | boolean | null;
  billingState?: string | null;
  dateFrom?: string | null;
  dateTo?: string | null;
}

/** URL → pannello. Uno stato di fatturazione sconosciuto vale «tutti». */
export function toWorkReportFilterValues(
  search: WorkReportFilterSearch
): WorkReportFilterValues {
  return {
    companyId: toFilterFormValue(search.companyId),
    contractId: toFilterFormValue(search.contractId),
    commessaId: toFilterFormValue(search.commessaId),
    technicianId: toFilterFormValue(search.technicianId),
    mine: isMineOn(search.mine) ? true : null,
    billingState: isBillingState(search.billingState)
      ? search.billingState
      : LIST_FILTER_ALL,
    period: toPeriodFormValue(search.dateFrom, search.dateTo),
  };
}

/** Pannello → URL. */
export function toWorkReportUrlParams(values: WorkReportFilterValues) {
  const period = toPeriodParams(values.period);
  return {
    companyId: toFilterParam(values.companyId),
    contractId: toFilterParam(values.contractId),
    commessaId: toFilterParam(values.commessaId),
    technicianId: toFilterParam(values.technicianId),
    mine: values.mine ? true : null,
    billingState: toFilterParam(values.billingState),
    dateFrom: period.from ?? null,
    dateTo: period.to ?? null,
  };
}

/** Il pannello come campo di `useSearchParamsForm`: i parametri che legge e scrive. */
export const WORK_REPORT_FILTER_FIELDS = panelSearchFields(
  panelSearchParam({
    keys: [
      'companyId',
      'contractId',
      'commessaId',
      'technicianId',
      'mine',
      'billingState',
      'dateFrom',
      'dateTo',
    ],
    toValues: toWorkReportFilterValues,
    toUrlParams: toWorkReportUrlParams,
    emptyValues: EMPTY_WORK_REPORT_FILTERS,
  })
);

/** Sezioni filtrabili dall'utente: senza, il filtro non esiste. */
export interface WorkReportFilterSections {
  contract: boolean;
  commessa: boolean;
  /** Fatture: senza, «In bozza fattura» vale «In fattura». */
  documents: boolean;
}

/**
 * Toglie i filtri di sezioni non concesse (un deep link, un «Vedi tutti»
 * della commessa aperto da chi poi ha perso il permesso): il server
 * risponderebbe 403 e la pill mostrerebbe un filtro che non si può
 * modificare. «In bozza fattura» senza la sezione Fatture diventa «In
 * fattura», come fa il server. Vale per lista, conteggio live, badge e pill.
 */
export function withoutHiddenSectionFilters(
  values: WorkReportFilterValues,
  sections: WorkReportFilterSections
): WorkReportFilterValues {
  return {
    ...values,
    contractId: sections.contract ? values.contractId : LIST_FILTER_ALL,
    commessaId: sections.commessa ? values.commessaId : LIST_FILTER_ALL,
    billingState: isBillingState(values.billingState)
      ? toViewerWorkReportBillingState(values.billingState, sections)
      : values.billingState,
  };
}

/** Pannello → query della lista (tabella e conteggio live). */
export function toWorkReportQueryParams(
  values: WorkReportFilterValues
): Pick<
  FetchWorkReportsDto,
  | 'companyId'
  | 'contractId'
  | 'commessaId'
  | 'technicianId'
  | 'mine'
  | 'billingState'
  | 'dateFrom'
  | 'dateTo'
> {
  const {
    companyId,
    contractId,
    commessaId,
    technicianId,
    billingState,
    dateFrom,
    dateTo,
  } = toWorkReportUrlParams(values);
  return {
    ...(companyId ? { companyId } : {}),
    ...(contractId ? { contractId } : {}),
    ...(commessaId ? { commessaId } : {}),
    ...(technicianId ? { technicianId } : {}),
    ...(values.mine ? { mine: true } : {}),
    ...(isBillingState(billingState) ? { billingState } : {}),
    ...(dateFrom ? { dateFrom } : {}),
    ...(dateTo ? { dateTo } : {}),
  };
}

export function countActiveWorkReportFilters(
  values: WorkReportFilterValues
): number {
  return [
    !!toFilterParam(values.companyId),
    !!toFilterParam(values.contractId),
    !!toFilterParam(values.commessaId),
    !!toFilterParam(values.technicianId),
    !!values.mine,
    !!toFilterParam(values.billingState),
    hasPeriod(values.period),
  ].filter(Boolean).length;
}

interface FilterChipsInput {
  values: WorkReportFilterValues;
  /** Nome del cliente se noto. */
  companyName?: string | null;
  /** Codice del contratto se noto. */
  contractName?: string | null;
  /** Nome del tecnico se noto. */
  technicianName?: string | null;
  /** Nome della commessa se noto (elenco commesse o righe caricate). */
  commessaName?: string | null;
  /** Chi guarda vede le Fatture: senza, «In fattura» al posto di «Fatturato». */
  documents: boolean;
  t: TFunction;
  onRemove: (field: keyof WorkReportFilterValues) => void;
}

/**
 * Pill dei filtri APPLICATI della lista rapportini, nell'ordine del pannello.
 * Il filtro commessa arriva anche dal pannello della commessa e si toglie
 * dalla pill come gli altri.
 */
export function buildWorkReportFilterChips({
  values,
  companyName,
  contractName,
  commessaName,
  technicianName,
  documents,
  t,
  onRemove,
}: FilterChipsInput): ActiveFilterChip[] {
  const chips: ActiveFilterChip[] = [];
  if (toFilterParam(values.companyId)) {
    chips.push({
      key: 'companyId',
      label: t('work_reports.filters.customer'),
      value: companyName || t('work_reports.filters.customer_selected'),
      onRemove: () => onRemove('companyId'),
    });
  }
  if (toFilterParam(values.contractId)) {
    chips.push({
      key: 'contractId',
      label: t('work_reports.filters.contract'),
      value: contractName || t('work_reports.filters.contract_selected'),
      onRemove: () => onRemove('contractId'),
    });
  }
  if (toFilterParam(values.commessaId)) {
    chips.push({
      key: 'commessaId',
      label: t('work_reports.filters.commessa'),
      value: commessaName || t('work_reports.filters.commessa_selected'),
      onRemove: () => onRemove('commessaId'),
    });
  }
  if (toFilterParam(values.technicianId)) {
    chips.push({
      key: 'technicianId',
      label: t('work_reports.filters.technician'),
      value: technicianName || t('work_reports.filters.technician_selected'),
      onRemove: () => onRemove('technicianId'),
    });
  }
  if (values.mine) {
    chips.push({
      key: 'mine',
      label: t('work_reports.filters.mine'),
      value: t('work_reports.filters.mine_on'),
      onRemove: () => onRemove('mine'),
    });
  }
  if (isBillingState(values.billingState)) {
    chips.push({
      key: 'billingState',
      label: t('work_reports.filters.billing'),
      value: t(billingStateLabelKey(values.billingState, { documents })),
      onRemove: () => onRemove('billingState'),
    });
  }
  if (hasPeriod(values.period)) {
    chips.push({
      key: 'period',
      label: t('work_reports.filters.period'),
      value: formatPeriodChipValue(values.period, t),
      onRemove: () => onRemove('period'),
    });
  }
  return chips;
}
